/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1692316800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Aug 18 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mixpanel-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "StatusCodeError: 400 - invalid project id for cluster",
          "depth": 1,
          "id": "statuscodeerror-400---invalid-project-id-for-cluster"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol",
      pre: "pre",
      img: "img"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you pull data stored in Mixpanel Cohorts and push it to downstream destinations. Connecting Hightouch to Mixpanel requires creating a Mixpanel service account. To create one, follow ", _jsx(_components.a, {
          href: "https://developer.mixpanel.com/reference/service-accounts",
          children: "Mixpanel's instructions"
        }), ". Keep your service account name and password ready for use in the Hightouch UI."]
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started in Hightouch, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Mixpanel Cohorts"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Mixpanel Cohorts"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Service account username"
          }), ": This is name of the service account you previously created."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Service account secret"
          }), ": The secret for the service account specified above."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Region"
          }), ": By default, Hightouch assumes that your Mixpanel data is stored in the United States, in which case you must leave this field empty.\nOtherwise, you can use this field to override that behavior by specifying a different region.\nFor example, insert ", _jsx(_components.code, {
            children: "eu"
          }), " if your data is located in the European Union.\nYou can find your Mixpanel region by looking at the ", _jsx(_components.strong, {
            children: "Data Residency"
          }), " field in your ", _jsx(_components.a, {
            href: "https://docs.mixpanel.com/docs/admin/organizations-projects/manage-projects#viewing-project-information",
            children: "Mixpanel Project Settings"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Mixpanel Cohorts as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Mixpanel."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models?from=docs",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the Mixpanel Cohorts source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the Mixpanel ", _jsx(_components.strong, {
            children: "data source"
          }), ", ", _jsx(_components.strong, {
            children: "project"
          }), ", and ", _jsx(_components.strong, {
            children: "cohort"
          }), " you want to use. The project and cohort populate based on your service account credentials. Check your credentials and click the refresh button if you don't see the values you expect."]
        }), "\n", _jsx(_components.li, {
          children: "Choose whether to include users without profile data in the query results."
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", "\n", _jsx(_components.h3, {
        id: "statuscodeerror-400---invalid-project-id-for-cluster",
        children: "StatusCodeError: 400 - invalid project id for cluster"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["You may receive this error if your Mixpanel ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: _jsx(_components.strong, {
            children: "region"
          })
        }), " is incorrect:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-txt",
          children: "StatusCodeError: 400 {\"request\":\"/api/2.0/engage?project_id=XXXXXXX\",\"error\":\"request failed with error: invalid project id for cluster....\"}\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch assumes that your Mixpanel data is stored in the United States, in which case you must leave the ", _jsx(_components.strong, {
          children: "Region"
        }), " field empty.\nOtherwise, you can use this field to override that behavior by specifying a different region.\nFor example, insert ", _jsx(_components.code, {
          children: "eu"
        }), " if your data is located in the European Union."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-mixpanel-region.png",
          alt: "Mixpanel region"
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
